// Provenance: repo:tds-reconciliation-portal:frontend/src/components/ProcessingPanel.tsx
// (ADR-0005) — restyled (ADR-0010). The honest-ETA rule carries: only a previous run's
// measured step durations drive an estimate; the first run says "estimating…" instead of a
// made-up number that would certainly be wrong.
import { CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { Run } from '../api/types'

// descriptive step labels (user requirement: say what is actually happening)
const STEP_LABELS: Record<string, string> = {
  parse_26as: 'Extracting data from Form 26AS',
  parse_ledger: 'Analyzing the books ledger',
  party_matching: 'Party-wise analysis',
  section_analysis: 'Section-wise analysis',
  txn_matching: 'Transaction-wise analysis',
  reports: 'Generating reports',
}

function fmtSecs(s: number): string {
  if (s < 0) return '0s'
  if (s >= 90) return `${Math.round(s / 60)}m ${Math.round(s % 60)}s`
  return `${Math.round(s)}s`
}

function Bar({ value, animated = false, tone = 'bg-primary' }: {
  value: number
  animated?: boolean
  tone?: string
}) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
      <div
        className={`h-full rounded ${tone} ${animated ? 'animate-pulse' : ''}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )
}

export function ProcessingPanel({ run, lastDurations }: {
  run: Run
  lastDurations?: Record<string, number> | null
}) {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [])

  const steps = run.progress?.steps ?? Object.keys(STEP_LABELS).map((name) => ({
    name,
    status: 'pending' as const,
    started_at: null,
    finished_at: null,
  }))
  const hasHistory = !!lastDurations && Object.keys(lastDurations).length >= 4
  const est = (name: string) => lastDurations?.[name] ?? 8
  const now = Date.now()

  const firstStart = steps.find((s) => s.started_at)?.started_at
  const elapsed = firstStart ? (now - Date.parse(firstStart)) / 1000 : 0

  const nDone = steps.filter((s) => s.status === 'done').length
  let doneEst = 0
  let totalEst = 0
  let remaining = 0
  for (const s of steps) {
    const e = est(s.name)
    totalEst += e
    if (s.status === 'done') {
      doneEst += e
    } else if (s.status === 'running' && s.started_at) {
      const inStep = (now - Date.parse(s.started_at)) / 1000
      // credit at most the full estimate; if the step overruns, remaining
      // grows honestly instead of pinning the display at "0s left"
      doneEst += Math.min(inStep, e)
      remaining += inStep < e ? e - inStep : e * 0.25
    } else {
      remaining += e
    }
  }
  const overall = hasHistory && totalEst
    ? Math.min(99, Math.round((doneEst / totalEst) * 100))
    : Math.min(99, Math.round((nDone / steps.length) * 100))
  const etaText = hasHistory
    ? `~${fmtSecs(remaining)} remaining`
    : 'estimating… (first run in this period)'

  return (
    <div className="rounded-md border border-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-bold">
          <Loader2 size={18} className="animate-spin" />
          Processing — {fmtSecs(elapsed)} elapsed, {etaText}
        </p>
        <span className="rounded bg-foreground px-2 py-0.5 text-xs font-semibold text-background">
          {nDone}/{steps.length} steps
        </span>
      </div>

      <div className="space-y-2">
        {steps.map((s) => {
          const e = est(s.name)
          const inStep = s.status === 'running' && s.started_at
            ? (now - Date.parse(s.started_at)) / 1000 : 0
          const value = s.status === 'done'
            ? 100
            : s.status === 'running'
              ? hasHistory ? Math.min(95, (inStep / e) * 100) : 50
              : 0
          return (
            <div key={s.name}>
              <div className="mb-0.5 flex items-center justify-between text-sm">
                <span className={s.status === 'running' ? 'font-bold' : 'font-medium'}>
                  {STEP_LABELS[s.name] ?? s.name}
                </span>
                <span className="flex items-center gap-1 text-xs">
                  {s.status === 'done' && (
                    <span className="flex items-center gap-1 font-semibold text-emerald-700">
                      <CheckCircle2 size={14} /> Done
                    </span>
                  )}
                  {s.status === 'running' && (
                    <span className="font-semibold text-primary">{fmtSecs(inStep)}</span>
                  )}
                  {s.status === 'pending' && <Clock size={14} className="text-muted-foreground" />}
                </span>
              </div>
              <Bar
                value={value}
                animated={s.status === 'running'}
                tone={s.status === 'done' ? 'bg-emerald-600' : 'bg-primary'}
              />
            </div>
          )
        })}
      </div>

      <div className="mb-1 mt-4 flex items-center justify-between text-sm">
        <span className="font-semibold">Overall progress</span>
        <span className="font-bold">{overall}%</span>
      </div>
      <Bar value={overall} tone="bg-cyan-600" />
    </div>
  )
}
